import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { NodeDefinition, TreeModule, TreeNode } from '@calibrant-tree';
import { AccountDto, DeveloperDto, SiteDto } from '@dto';
import { DeveloperService, TreeService } from '@services';

@Component({
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  imports: [TreeModule, TranslatePipe, MatProgressSpinner],

  selector: 'monitor-ui-tree',
  styleUrls: ['./tree.component.scss'],
  templateUrl: './tree.component.html',
})
export class TreeComponent implements OnDestroy, OnInit {
  loading = true;

  private _onDestroy = new Subject<void>();
  expandedNodes = new Set<TreeNode<DeveloperDto | AccountDto | SiteDto>>();
  selectedNodes = new Set<TreeNode<DeveloperDto | AccountDto | SiteDto>>();
  developers: DeveloperDto[] = [];
  treeData: TreeNode<DeveloperDto | AccountDto | SiteDto>[] = [];
  filterValue = '';
  dataSource = new MatTreeNestedDataSource<TreeNode<never>>();
  selection: TreeNode<DeveloperDto | AccountDto | SiteDto>[] = [];
  shownExpandedNodes: TreeNode<DeveloperDto | AccountDto | SiteDto>[] = [];
  clickedNode: TreeNode<DeveloperDto | AccountDto | SiteDto>;
  maxSelections = 1;
  labelIdentifier = 'displayLabel';
  iconIdentifier = 'spaceType';
  SITE_DR_TYPE = 'Site';
  ACCOUNT_DR_TYPE = 'Account';
  disabledNodeAttribute = 'spaceType';
  disabledNodeValues: string[] = ['Developer'];
  searchProperties: string[] = ['displayLabel'];
  disabledNodeDefinitions: NodeDefinition = {
    [this.disabledNodeAttribute]: this.disabledNodeValues,
  };

  constructor(
    private router: Router,
    private developerService: DeveloperService,
    private treeService: TreeService,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.developerService
      .getDevelopers$()
      .pipe(takeUntil(this._onDestroy))
      .subscribe(developers => {
        this.developers.push(...developers);
        this.treeData = this.developers;
        this.dataSource.data = this.treeData;

        if (this.developers[0]) {
          this.expandedNodes.add(this.developers[0]);
        }

        // TODO: might break like this, make this selection more dynamic to be the first selectable thing in the list
        if (this.developers[0]?.children && this.developers[0].children.length > 0) {
          this.expandedNodes.add(this.developers[0].children[0]);
        }
        this.loading = false;
      });
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
  }

  onAccountClicked(account: TreeNode<DeveloperDto | AccountDto | SiteDto>): void {
    this.ngZone.run(() => {
      this.treeService.selectAccountInList(account as AccountDto);
      this.router.navigate([`/details/${account.id}/view-account`], {
        queryParams: { accountId: account['id'] },
      });
    });
  }

  onSiteClicked(site: TreeNode<DeveloperDto | AccountDto | SiteDto>): void {
    this.ngZone.run(() => {
      this.router.navigate([`/details/${site.id}/view-site`], {
        queryParams: { siteId: site['id'] },
      });
    });
  }

  expansionHandler(nodes: TreeNode<DeveloperDto | AccountDto | SiteDto>[]): void {
    this.shownExpandedNodes = nodes;
  }

  selectionHandler(nodes: TreeNode<DeveloperDto | AccountDto | SiteDto>[]): void {
    if (!nodes[0]) {
      return;
    }

    if (nodes[0]['drType'] == this.SITE_DR_TYPE) {
      this.onSiteClicked(nodes[0]);
    }

    if (nodes[0]['drType'] == this.ACCOUNT_DR_TYPE) {
      this.onAccountClicked(nodes[0]);
    }
  }

  clickedHandler(node: TreeNode<DeveloperDto | AccountDto | SiteDto>) {
    this.clickedNode = node;
  }
}
